import type { ScopeData } from "../state/useScopeData";
import { PROJECT_CONTEXT_LIMITS } from "../api/projectContextGuidance";

export { PROJECT_CONTEXT_LIMITS };
export interface ProjectContext {
  schema_version: 1;
  scope_node_id: string;
  client_scope_node_id: string | null;
  client_name: string | null;
  project_name: string;
  revision: number;
  products: string[];
  objective: string;
  updated_at: string | null;
  can_edit: boolean;
  consumers: {
    client_intelligence: "available" | "not_connected";
    perlab: "available" | "not_connected";
    kearney: "not_connected";
  };
}
export interface ProjectContextDraft { products: string[]; objective: string }
export class InvalidProjectContext extends Error {
  constructor() { super("Project context could not be verified."); }
}
function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new InvalidProjectContext();
  return value as Record<string, unknown>;
}
function text(value: unknown, max: number, empty = false): string {
  if (typeof value !== "string" || value.length > max || value !== value.trim() || (!empty && !value)) throw new InvalidProjectContext();
  return value;
}
export function validateProjectContextDraft(value: ProjectContextDraft): boolean {
  return Array.isArray(value.products) && value.products.length <= PROJECT_CONTEXT_LIMITS.products
    && value.products.every(item => typeof item === "string" && item === item.trim() && item.length > 0 && item.length <= PROJECT_CONTEXT_LIMITS.productLength)
    && new Set(value.products.map(item => item.toLowerCase())).size === value.products.length
    && typeof value.objective === "string" && value.objective === value.objective.trim()
    && value.objective.length <= PROJECT_CONTEXT_LIMITS.objectiveLength;
}
export function parseProjectContext(value: unknown, scopeId: string): ProjectContext {
  const row = record(value), consumers = record(row.consumers);
  if (row.schema_version !== 1 || row.scope_node_id !== scopeId || !Number.isSafeInteger(row.revision)
    || Number(row.revision) < 0 || typeof row.can_edit !== "boolean"
    || !Array.isArray(row.products) || !row.products.every(item => typeof item === "string")
    || typeof row.objective !== "string") throw new InvalidProjectContext();
  const draft = { products: row.products as string[], objective: row.objective };
  if (!validateProjectContextDraft(draft)) throw new InvalidProjectContext();
  const clientId = row.client_scope_node_id === null ? null : text(row.client_scope_node_id, 256);
  const clientName = row.client_name === null ? null : text(row.client_name, 512);
  if ((clientId === null) !== (clientName === null)) throw new InvalidProjectContext();
  const revision = Number(row.revision);
  let updatedAt: string | null = null;
  if (revision === 0) {
    if (row.updated_at !== null || draft.products.length || draft.objective) throw new InvalidProjectContext();
  } else {
    updatedAt = text(row.updated_at, 64);
    if (!/^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(updatedAt) || !Number.isFinite(Date.parse(updatedAt))) throw new InvalidProjectContext();
  }
  const consumer = (key: string): "available" | "not_connected" => {
    const state = consumers[key];
    if (state !== "available" && state !== "not_connected") throw new InvalidProjectContext();
    return state;
  };
  if (consumers.kearney !== "not_connected") throw new InvalidProjectContext();
  return { schema_version: 1, scope_node_id: scopeId, client_scope_node_id: clientId, client_name: clientName,
    project_name: text(row.project_name, 512), revision, ...draft, updated_at: updatedAt, can_edit: row.can_edit,
    consumers: { client_intelligence: consumer("client_intelligence"), perlab: consumer("perlab"), kearney: "not_connected" } };
}
export function projectContextClient(fetchScoped: ScopeData["fetchScoped"], scopeId: string) {
  return {
    async read(signal: AbortSignal): Promise<ProjectContext> {
      return parseProjectContext(await fetchScoped<unknown>("/project-context", { init: { signal } }), scopeId);
    },
    async save(revision: number, draft: ProjectContextDraft, signal: AbortSignal): Promise<ProjectContext> {
      if (!Number.isSafeInteger(revision) || revision < 0 || !validateProjectContextDraft(draft)) throw new InvalidProjectContext();
      const saved = parseProjectContext(await fetchScoped<unknown>("/project-context", { init: {
        method: "PUT", headers: { "Content-Type": "application/json" }, signal,
        body: JSON.stringify({ expected_revision: revision, products: draft.products, objective: draft.objective }),
      } }), scopeId);
      if (saved.revision !== revision + 1) throw new InvalidProjectContext();
      return saved;
    },
  };
}
